import { httpClient } from './api';

// ─── Types ──────────────────────────────────────────────

export type ReasonCategory = 'umzug' | 'zeitlich' | 'finanziell' | 'unzufrieden' | 'gesundheitlich' | 'sonstiges';
export type KontingentHandling = 'forfeit' | 'use_until_effective' | 'partial_refund';
export type KuendigungStatus = 'draft' | 'confirmed' | 'effective' | 'withdrawn';

export interface KuendigungPreview {
  teilnahme: {
    id: number;
    kursId: number;
    subprofileId: string | null;
    effectiveFrom: string;
    effectiveTo: string | null;
    status: string;
  };
  kurs: {
    id: number;
    name: string;
    fachName: string | null;
    teacherName: string | null;
    standortName: string | null;
    standortId: number | null;
    weekday: number | null;
    startTime: string | null;
    durationMin: number;
  };
  student: {
    name: string | null;
    email: string | null;
    wpUserId: number | null;
  };
  effectiveDateCalc: {
    earliestDate: string;
    kuendigungsfristDays: number;
    fristType: string | null;
    fristValue: number | null;
    mindestlaufzeitOk: boolean;
    mindestlaufzeitEndsAt: string | null;
  };
  kontingent: {
    remainingUnits: number;
    totalUnits: number;
    usedUnits: number;
    totalPaidCents: number;
    /** Erstattung für die Resteinheiten bei Teilerstattung (T-0173); fehlt bei Altpaketen. */
    refundCents?: number | null;
    unitPriceCents?: number | null;
    packageName: string | null;
  } | null;
  existingKuendigung: KuendigungDTO | null;
}

export interface CreateKuendigungPayload {
  kursTeilnahmeId: number;
  effectiveDate?: string;
  reasonCategory?: ReasonCategory;
  reasonText?: string;
  kontingentHandling?: KontingentHandling;
  notes?: string;
  sendEmail?: boolean;
  emailContent?: string;
  emailSubject?: string;
}

export interface KuendigungDTO {
  id: number;
  kursTeilnahmeId: number;
  kursId: number;
  subprofileId: string;
  requestedAt: string;
  effectiveDate: string;
  kuendigungsfristDays: number | null;
  earliestPossibleDate: string | null;
  status: KuendigungStatus;
  initiatedBy: 'office' | 'customer';
  createdByUserId: number | null;
  reasonCategory: ReasonCategory | null;
  reasonText: string | null;
  kontingentHandling: KontingentHandling;
  remainingUnitsAtCancellation: number | null;
  refundAmountCents: number | null;
  refundCurrency: string;
  confirmationEmailSentAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  communicationWarnings?: string[];
  // Liefert das Backend schon immer mit; die Übersicht braucht sie (T-0075).
  studentName?: string | null;
  studentEmail?: string | null;
  wpUserId?: number | null;
  standortId?: number | null;
  standortName?: string | null;
  kursName?: string | null;
  /** Wem der Vertrag gehört — die Person hinter dem Lernenden (T-0174). */
  kontoinhaberName?: string | null;
  kontoinhaberEmail?: string | null;
}

export interface KuendigungHistoryEntry {
  id: number;
  kuendigungId: number;
  action: string;
  oldStatus: string | null;
  newStatus: string | null;
  actorType: string;
  actorId: number | null;
  actorName: string | null;
  actorEmail: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

export interface FristConfig {
  id: number;
  standortId: number | null;
  unterrichtsformId: number | null;
  standortName: string | null;
  unterrichtsformName: string | null;
  fristType: 'days_fixed' | 'zum_monatsende' | 'zum_quartalsende' | 'zum_periodenende';
  fristValue: number;
  mindestlaufzeitMonths: number | null;
  aktiv: boolean;
}

// ─── API Functions ──────────────────────────────────────

export async function getKuendigungPreview(teilnahmeId: number): Promise<KuendigungPreview> {
  const res = await httpClient<KuendigungPreview>(`/api/kuendigungen/preview/${teilnahmeId}`);
  if (!res.data) throw new Error(res.error || 'Vorschau nicht verfügbar');
  return res.data;
}

export async function lookupTeilnahmeId(courseId: number, subprofileId: string): Promise<number> {
  const res = await httpClient<{ id: number }>(`/api/kurs-teilnahmen/lookup?courseId=${courseId}&subprofileId=${encodeURIComponent(subprofileId)}`);
  if (!res.data?.id) throw new Error(res.error || 'Keine aktive Teilnahme gefunden');
  return res.data.id;
}

export async function createKuendigung(payload: CreateKuendigungPayload): Promise<KuendigungDTO> {
  const res = await httpClient<KuendigungDTO>('/api/kuendigungen', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Kündigung konnte nicht erstellt werden');
  return res.data;
}

export async function listKuendigungen(standortId: number, status?: string): Promise<KuendigungDTO[]> {
  let url = `/api/kuendigungen?standortId=${standortId}`;
  if (status) url += `&status=${status}`;
  const res = await httpClient<KuendigungDTO[]>(url);
  return res.data || [];
}

export async function getKuendigung(id: number): Promise<KuendigungDTO> {
  const res = await httpClient<KuendigungDTO>(`/api/kuendigungen/${id}`);
  if (!res.data) throw new Error(res.error || 'Kündigung nicht gefunden');
  return res.data;
}

export async function withdrawKuendigung(id: number): Promise<KuendigungDTO> {
  const res = await httpClient<KuendigungDTO>(`/api/kuendigungen/${id}/withdraw`, { method: 'POST' });
  if (!res.data) throw new Error(res.error || 'Kündigung konnte nicht zurückgezogen werden');
  return res.data;
}

export async function getKuendigungHistory(id: number): Promise<KuendigungHistoryEntry[]> {
  const res = await httpClient<KuendigungHistoryEntry[]>(`/api/kuendigungen/${id}/history`);
  return res.data || [];
}

// ─── Frist Config API ───────────────────────────────────

export async function listFristConfigs(): Promise<FristConfig[]> {
  const res = await httpClient<FristConfig[]>('/api/kuendigungsfrist-config');
  return res.data || [];
}

export async function createFristConfig(data: Partial<FristConfig>): Promise<FristConfig> {
  const res = await httpClient<FristConfig>('/api/kuendigungsfrist-config', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.data) throw new Error(res.error || 'Konfiguration konnte nicht erstellt werden');
  return res.data;
}

export async function updateFristConfig(id: number, data: Partial<FristConfig>): Promise<FristConfig> {
  const res = await httpClient<FristConfig>(`/api/kuendigungsfrist-config/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!res.data) throw new Error(res.error || 'Konfiguration konnte nicht aktualisiert werden');
  return res.data;
}

export async function deleteFristConfig(id: number): Promise<void> {
  await httpClient(`/api/kuendigungsfrist-config/${id}`, { method: 'DELETE' });
}

// ─── Helpers ────────────────────────────────────────────

export const REASON_LABELS: Record<ReasonCategory, string> = {
  umzug: 'Umzug',
  zeitlich: 'Zeitliche Gründe',
  finanziell: 'Finanzielle Gründe',
  unzufrieden: 'Unzufriedenheit',
  gesundheitlich: 'Gesundheitliche Gründe',
  sonstiges: 'Sonstiges',
};

export const KONTINGENT_HANDLING_LABELS: Record<KontingentHandling, string> = {
  forfeit: 'Restguthaben verfallen lassen',
  use_until_effective: 'Bis Wirksamkeitsdatum nutzbar',
  partial_refund: 'Anteilige Erstattung',
};

export const STATUS_LABELS: Record<KuendigungStatus, string> = {
  draft: 'Entwurf',
  confirmed: 'Bestätigt',
  effective: 'Wirksam',
  withdrawn: 'Zurückgezogen',
};

export const FRIST_TYPE_LABELS: Record<string, string> = {
  days_fixed: 'Feste Tage',
  zum_monatsende: 'Zum Monatsende',
  zum_quartalsende: 'Zum Quartalsende',
  zum_periodenende: 'Zum Ende des Beitragszeitraums',
};
